import React, { useCallback, useEffect, useMemo, useState } from 'react';

// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import {
  type Types,
  getEnabledElement,
  getRenderingEngine,
} from '@cornerstonejs/core';
// eslint-disable-next-line import/no-extraneous-dependencies -- '@cornerstonejs/core' should be listed in the project's dependencies. Run 'npm i -S @cornerstonejs/core' to add it
import { worldToImageCoords } from '@cornerstonejs/core/dist/esm/utilities';
import { ToolGroupManager } from '@cornerstonejs/tools';
import { Events } from '@cornerstonejs/tools/dist/esm/enums';
import styled from '@emotion/styled';
import {
  faClipboardCheck,
  faRotateRight,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import { CircularProgress, Popover } from '@mui/material';
import { Button } from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import type { RootStateOrAny } from 'react-redux';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import { isHoveringUserValidationFindingAtom } from '@/atoms/atoms';

import { RblPopup } from '../../../../common-components/composed/rblPopup/RblPopup';
import { SegmentationsHatchPatterns } from '../../../../common-components/elements/icon/SegmentationsHatchPatterns';
import { VideaTypography } from '../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  AREA_OF_CONCERN,
  CALCULUS,
  CARIES,
  DEFAULT_BRIGHTNESS_VALUE,
  DEFAULT_CONTRAST_VALUE,
} from '../../../../constants';
import { usePreferences } from '../../../../react-query/usePreferences';
import {
  CariesDepth,
  useGetImageWithSegmentations,
} from '../../../../react-query/useSegmentations';
import { useIncipient } from '../../../../routes/IncipientContext';
import { annotationStyles } from '../../../../theme/annotationStyles';
import {
  blueBlack640,
  red400,
  yellow500,
} from '../../../../theme/videa-style-tokens';
import { renderingEngineAtom } from '../../../../utils/cornerstone/cornerstoneAtoms';
import {
  RENDERING_ENGINE,
  thumbnailToolGroupId,
  toolGroupId,
} from '../../../../utils/cornerstone/initializeCornerstone';
import type { Source } from '../../../../utils/cornerstone/types';
import { useCornerstone } from '../../../../utils/cornerstone/useCornerstone';
import { useDicomCanvas } from '../../../../utils/cornerstone/useDicomCanvas';
import { isValidToothNumber } from '../../../../utils/finding';
import { useDevtoolsShortcut } from '../../../../utils/hooks/useDevtoolsShortcut';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { findRBLAnnotationIndex } from '../../../../utils/rbl';
import {
  computeCornerstoneRoiFromSavedAnnotation,
  computeCursorClass,
  computeImagePath,
  findHoveredAnnotation,
  findHoveredRblPointAnnotation,
  getAugmentedPathologiesForImage,
  shouldTreatImageAsPA,
} from '../../annotation-utils/annotation-utils';
import { isWithinSensitivityRange } from '../../annotation-utils/incipient';
import AnnotatorConstants from '../../annotator.constants';
import {
  casePresentationModeAtom,
  selectedFindingImageIdAtom,
  selectedFindingsAtom,
} from '../../casePresentation/atoms';
import { ShowOnlyFindingButton } from '../../casePresentation/ShowOnlyFindingButton';
import { toothPartsTogglesAtom } from '../../findingSidebar/atoms';
import { confidenceScoreFilterAtom } from '../../findingSidebar/confidenceScoreFilter/atoms';
import { useIsWithinConfidenceThreshold } from '../../findingSidebar/confidenceScoreFilter/helpers';
import { useFindingSidebarTranslations } from '../../findingSidebar/constants';
import FindingValidationSelector from '../../findingValidationSelector/FindingValidationSelector';
import type { BoundingBoxFinding } from '../../types';
import { useImageQualityAssessment } from '../hooks/useImageQualityAssessment';
import {
  imageQualityAssessmentOverlayOpenAtom,
  isHQEnabledAtom,
  showSegmentationAtom,
} from '../imageToolbar/atoms';
import { ModelErrorIcon } from '../ModelErrorIcon';
import { useRotation } from '../RotationContext';

import { AREA_OF_CONCERN_SINGULAR } from './constants';
import type { FindingViewerProps } from './FindingViewer.types';
import ImageChipStatus from './ImageChipStatus';
import { getModelErrorMessage, getPathologiesToDisplay } from './utils';

const StyleWrapper = styled.div`
  // Use makeStyles to override Material-UI colors and properties
  .findingPopover {
    & .MuiPopover-paper {
      background-color: ${blueBlack640};
    }
  }
  .progressBar {
    color: white;
  }
  .loadingSpinner {
    z-index: 5;
    position: absolute;
  }
  .errorIcon {
    color: red;
    position: absolute;
    z-index: 100;
  }
  .indicatorIcon {
    position: absolute;
    z-index: 100;
    top: 0.0625em;
    left: 0.5em;
    height: 0.5em;
    width: auto;
    &.isLandscape {
      top: 0.375em;
      left: 0.25em;
    }
  }
  .crownIcon {
    fill: ${red400};
  }
  .incipientIcon {
    fill: ${yellow500};
  }
  .cameraIcon {
    color: orange;
  }
  .magnifyMode {
    cursor: zoom-in !important;
  }
  .drawMode {
    cursor: crosshair !important;
  }
  &.cornerstoneContainer {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 0.25em;
    height: 100%;
    .default {
      cursor: pointer !important;
      &.user-annotation {
        cursor: crosshair !important;
      }
    }
    @media (max-width: 800px) {
      height: 100%;
      margin: 0;
    }
  }

  ${annotationStyles}
`;

const FindingPopoverContainers = styled.div`
  &.hoverPopupContainer {
    padding: 0.5em;
    background-color: ${blueBlack640};
    color: white;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.25em;
  }
  &.findingPopoverInnerContainer {
    padding-left: 1em;
    padding-right: 1em;
    padding-bottom: 1em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: column;
    position: relative;
    overflow-y: hidden;
    background-color: ${blueBlack640};
    color: white;
  }
  &.findingPopoverToothNumberContainer {
    text-align: center;
    padding: 0.5em;
    display: flex;
    background-color: ${blueBlack640};
    color: white;
  }
  .popupHeaderText {
    color: white;
    font-size: 15px;
    font-weight: 600;
  }
  .popupCloseIcon {
    &:hover {
      cursor: pointer;
    }
    font-size: 16px;
  }
  &.toothDecayInfo {
    padding-left: 12px;
    padding-top: 5px;
    background-color: ${blueBlack640};
    color: white;
  }
`;
/**
 * This component renders our imaging file with its findings, using effects for the following lifecycle:
 * 1st: Once cornerstone is enabled (in the parent), get the actual tools we can use and "enable" them, adding them to our canvas
 * 2nd: Whenever the image path changes, update the image that is displaying. Then, add the findings
 *
 * We also watch the following: the opacity flag, to update the viewport (invert contrast)
 * The viewer mode, to switch the active tools between those used for drawing and viewing
 * The active image object, so that when a new annotation completes, we can add it to our finding list
 */
const FindingViewer: React.FC<FindingViewerProps> = (props) => {
  const {
    activeImage,
    addAnnotation,
    annotationDrawType,
    deleteUserAnnotation,
    enabledFindingTypes,
    hasHoveredFinding,
    isThumbnail,
    changeViewerMode,
    trackCornerstoneMouseEvent,
    updateFinding,
    viewerMode,
    patient,
    user,
    sidebarIsNotCollapsed,
    shouldDisplayRootTips,
    currentlyHoveredImageId,
    onSetBrightnessValue,
    onSetContrastValue,
    onSetIsOpacityInverted,
    onSetShouldPersistUserModifications,
    index,
    currentImageFilter,
  } = props;

  const { trackEvent } = useTrackEvent();

  const [dicomCanvas, dicomCanvasRef] = useDicomCanvas();
  const [modifiedFinding, setModifiedFinding] = useState(null);
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);

  const [findingState, setFindingState] = useState<{
    finding: BoundingBoxFinding | null;
    position: Types.Point2 | undefined;
  }>({ finding: null, position: undefined });

  const [hoveredRBLPoints, setHoveredRBLPoints] = useState<null | {
    x: number;
    y: number;
  }>(null);
  const [hoveredBox, setHoveredBox] = useState(null);
  const [hoveredFinding, setHoveredFinding] =
    useState<BoundingBoxFinding | null>(null);
  const isRenderingEngineInitialized = useAtomValue(renderingEngineAtom);
  const isHQEnabled = useAtomValue(isHQEnabledAtom);
  const renderingEngine =
    isRenderingEngineInitialized && getRenderingEngine(RENDERING_ENGINE);
  const practiceThreshold = useSelector(
    (state: RootStateOrAny) =>
      state.login.user.practice?.rblSettings?.templateName,
  );

  const isHoveringUserValidationFinding = useAtomValue(
    isHoveringUserValidationFindingAtom,
  );
  const isOverlayOpen = useAtomValue(imageQualityAssessmentOverlayOpenAtom);
  const showSegmentationBase = useAtomValue(showSegmentationAtom);
  const showSegmentation = showSegmentationBase && !isOverlayOpen;
  const showAnatomySegmentation = useAtomValue(
    toothPartsTogglesAtom,
  ).tooth_anatomy;
  const isImageWithSegmentations =
    useGetImageWithSegmentations(activeImage?.imageId) &&
    activeImage.detectedImageType !== 'COLOR_IMAGE';
  const viewportId = isThumbnail ? 'fmx' + index : 'image-viewer';

  const { data: prefs } = usePreferences();
  const isSegmentationEnabled = Boolean(prefs?.segmentationEnabled);
  const shouldUseClickBasedInteraction = Boolean(
    prefs?.enableClickBasedFindingInteraction,
  );

  const onResize = useCallback(() => {
    const viewportForImage =
      renderingEngine &&
      (renderingEngine.getViewport(viewportId) as
        | Types.IStackViewport
        | undefined);

    try {
      if (
        activeImage &&
        dicomCanvas &&
        renderingEngine &&
        getEnabledElement(dicomCanvas as HTMLDivElement) &&
        viewportForImage &&
        viewportForImage.getCurrentImageId() &&
        viewportForImage.getImageData()
      ) {
        renderingEngine.resize();
      }
    } catch (error) {
      // Catch cornerstone error here. Hacky solution until we can get cornerstone core cloned locally and resolve type errors
    }
  }, [activeImage, dicomCanvas, renderingEngine, viewportId]);

  const { ref: containerRef } = useResizeDetector({
    handleHeight: true,
    handleWidth: true,
    skipOnMount: true,
    refreshMode: undefined,
    onResize,
  });

  const {
    setIsRotationButtonHovered,
    rotateImage,
    setRerunPopupMargin,
    closeSnackbarErrorOnSingleImage,
  } = useRotation();

  let source: Source = isThumbnail
    ? 'multi-image-viewer'
    : 'single-image-viewer';
  if (
    currentImageFilter === AnnotatorConstants.ODONTOGRAM ||
    (!!currentImageFilter && isValidToothNumber(currentImageFilter))
  ) {
    source = 'odontogram-tray';
  }

  const {
    isCornerstoneReady,
    imageLoadStatus,
    loadImageFileIntoCanvas,
    drawFindings,
  } = useCornerstone({
    dicomCanvasRef: dicomCanvas,
    isThumbnail,
    activeImage,
    viewportId,
    source,
  });

  const {
    QualityAssessmentWidget,
    handleOpenOverlay,
    shouldEnableImageQualityAssessment,
  } = useImageQualityAssessment({
    isThumbnail,
    imageLoadStatus,
    activeImage,
    isHQEnabled,
  });

  const isPAImage = shouldTreatImageAsPA(activeImage);

  // @ts-expect-error TS(2339): Property 'incipientThresholdRange' does not exist ... Remove this comment to see the full error message
  const { incipientThresholdRange } = useIncipient();

  const confidenceScoreThreshold = useAtomValue(confidenceScoreFilterAtom);

  const isWithinConfidenceThreshold = useIsWithinConfidenceThreshold();
  const { showDevtools } = useDevtoolsShortcut();
  const { typeToHeaderMap } = useFindingSidebarTranslations();

  const {
    cariesForImage: cariesFindings,
    prlForImage: prlFindings,
    rblForImage: rblFindings,
    userAnnotations,
    calculusFindings,
    restorativeAdjacentLucencyFindings,
  } = getAugmentedPathologiesForImage(activeImage);

  const predictedPathologies = useMemo(
    () => {
      // When overlay is open, filter out all findings
      if (isOverlayOpen) {
        return [];
      }

      const pathologies = getPathologiesToDisplay(enabledFindingTypes, {
        cariesFindings,
        prlFindings,
        calculusFindings,
        restorativeAdjacentLucencyFindings,
        userAnnotations,
      })?.filter((finding) =>
        isWithinConfidenceThreshold(
          finding.type,
          finding.confidenceScore,
          finding?.bin,
        ),
      );

      return pathologies;
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useMemo has missing dependencies: 'calculusFindings', 'cariesFindings', 'prlFindings', and 'restorativeAdjacentLucencyFindings'. Either include them or remove the dependency array. */
    [
      activeImage,
      enabledFindingTypes,
      isWithinConfidenceThreshold,
      isOverlayOpen,
    ],
  );

  const hoveredPrl = useMemo(
    () => predictedPathologies?.find((finding) => finding.id === hoveredBox),
    [hoveredBox, predictedPathologies],
  );

  const hoveredLine = useMemo(
    () => findHoveredRblPointAnnotation(hoveredRBLPoints, rblFindings),
    [hoveredRBLPoints, rblFindings],
  );

  useEffect(() => {
    if (viewerMode === AnnotatorConstants.MODES.MAGNIFYING) return;
    if (!shouldUseClickBasedInteraction) {
      setHoveredFinding(hoveredPrl || hoveredLine || null);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'viewerMode'. Either include it or remove the dependency array. */
  }, [hoveredPrl, hoveredLine, shouldUseClickBasedInteraction]);

  useEffect(() => {
    if (!isThumbnail) {
      closeSnackbarErrorOnSingleImage();
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'closeSnackbarErrorOnSingleImage'. Either include it or remove the dependency array. */
  }, [isThumbnail]);

  const isHqOnThumbnails =
    !!prefs?.enableHqOnThumbnails && !prefs?.hqOffByDefault;
  const isAnnotationPopoverOpen = shouldUseClickBasedInteraction
    ? !!findingState.finding
    : isPopoverHovered || !!hoveredFinding;
  const shouldShowHQImage = (!isThumbnail || isHqOnThumbnails) && isHQEnabled;
  const dicomPath = computeImagePath(activeImage, shouldShowHQImage);

  // hold the previous coordinates during a mouse down event; allows to compute the diff
  let prevMouseEvent: any;

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [sidebarIsNotCollapsed]);

  /**
   * When we have a nonempty dicom path, and cornerstone is loaded, we can load this image, using imageLoadStatus to do this once per image
   * After showing the image, we can then draw the findings
   */

  useEffect(() => {
    if (
      renderingEngine &&
      ToolGroupManager.getToolGroup(
        !isThumbnail ? toolGroupId : thumbnailToolGroupId,
      ) &&
      activeImage?.imageId &&
      dicomCanvas &&
      // This avoids a race condition when we load the image at first w/ HQ on -> off due to pref in single image view
      (isThumbnail || isHQEnabled !== undefined)
    ) {
      loadImageFileIntoCanvas(
        (!isThumbnail || isHqOnThumbnails) && isHQEnabled,
      );
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'loadImageFileIntoCanvas'. Either include it or remove the dependency array. */
  }, [
    dicomCanvas,
    renderingEngine,
    dicomPath,
    isHQEnabled,
    activeImage?.imageRerunAt,
    activeImage?.imageId,
    index,
    isThumbnail,
    currentImageFilter,
    isHqOnThumbnails,
  ]);

  useEffect(() => {
    if (isThumbnail) {
      setRerunPopupMargin(null);
    } else {
      setRerunPopupMargin(50);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'isThumbnail' and 'setRerunPopupMargin'. Either include them or remove the dependency array. */
  }, []);

  useEffect(() => {
    if (!isThumbnail) {
      const userModificationsState = activeImage?.userModifications;
      onSetBrightnessValue(
        userModificationsState?.brightnessValue
          ? userModificationsState.brightnessValue
          : DEFAULT_BRIGHTNESS_VALUE,
      );
      onSetContrastValue(
        userModificationsState?.contrastValue
          ? userModificationsState.contrastValue
          : DEFAULT_CONTRAST_VALUE,
      );
      onSetIsOpacityInverted(userModificationsState?.opacityInverted ?? false);
    }
    if (isThumbnail) {
      onSetShouldPersistUserModifications(false);
      onSetIsOpacityInverted(false);
      onSetBrightnessValue(DEFAULT_BRIGHTNESS_VALUE);
      onSetContrastValue(DEFAULT_CONTRAST_VALUE);
    }

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'activeImage?.userModifications', 'isHQFlagDisabled', 'setBrightnessValue', 'setContrastValue', 'setIsHQEnabled', 'setIsOpacityInverted', and 'setShouldPersistUserModifications'. Either include them or remove the dependency array. */
  }, [activeImage?.objectId, isThumbnail]);

  const resetAnnotationHoverStates = useCallback(() => {
    if (imageLoadStatus === 'loading' || !dicomCanvas) {
      return;
    }

    for (let j = 0; j < predictedPathologies.length; j += 1) {
      predictedPathologies[j].hover = false;
    }
    for (let i = 0; i < userAnnotations.length; i += 1) {
      userAnnotations[i].hover = false;
    }

    for (let i = 0; i < rblFindings.length; i += 1) {
      rblFindings[i].sides.forEach((rblSide: any) => {
        rblSide.points.forEach((point: any) => {
          point.hover = false;
        });
      });
    }
    drawFindings();
  }, [
    imageLoadStatus,
    dicomCanvas,
    predictedPathologies,
    userAnnotations,
    rblFindings,
    drawFindings,
  ]);

  const isCasePresentationMode = useAtomValue(casePresentationModeAtom);
  const selectedFinding = useAtomValue(selectedFindingsAtom);
  const selectedFindingImageId = useAtomValue(selectedFindingImageIdAtom);

  // Helper function to get current finding and position
  const getCurrentFindingAndPosition = useCallback(() => {
    if (shouldUseClickBasedInteraction) {
      return {
        finding: findingState.finding,
        position: findingState.position,
      };
    } else {
      return {
        finding: hoveredFinding,
        position: findingState.position, // Use the same position state for both modes
      };
    }
  }, [shouldUseClickBasedInteraction, findingState, hoveredFinding]);

  const cursorClass = computeCursorClass(viewerMode);

  const checkForHoveredFindings = useCallback(
    (e: any) => {
      const viewportForImage =
        renderingEngine && renderingEngine.getViewport(viewportId);
      if (!viewportForImage) {
        return;
      }
      let imageCoords: Types.Point2 | undefined;
      try {
        imageCoords = worldToImageCoords(
          viewportForImage.getCurrentImageId(),
          e.detail.currentPoints.world,
        );
      } catch (error) {
        return;
      }

      if (!imageCoords) {
        return;
      }
      const point = {
        x: imageCoords[0],
        y: imageCoords[1],
      };
      const pathologiesExist =
        predictedPathologies?.length ||
        userAnnotations?.length ||
        rblFindings?.length;
      if (
        !isThumbnail &&
        viewerMode !== AnnotatorConstants.MODES.MAGNIFYING &&
        pathologiesExist
      ) {
        let hoveredBoxAnnotation = findHoveredAnnotation(
          predictedPathologies,
          userAnnotations,
          point,
        );

        const meetsSensitivityRequirement =
          hoveredBoxAnnotation &&
          ((hoveredBoxAnnotation.type !== CARIES &&
            hoveredBoxAnnotation.type !== AREA_OF_CONCERN) ||
            isWithinSensitivityRange(
              hoveredBoxAnnotation,
              activeImage,
              incipientThresholdRange,
            ));

        const shouldShowFinding =
          meetsSensitivityRequirement &&
          isWithinConfidenceThreshold(
            hoveredBoxAnnotation.type,
            hoveredBoxAnnotation.confidenceScore,
            hoveredBoxAnnotation?.bin,
          );

        if (!shouldShowFinding) {
          hoveredBoxAnnotation = null;
        }

        const hoveredRblPointAnnotation = rblFindings?.length
          ? findHoveredRblPointAnnotation(point, rblFindings)
          : null;

        const isRblHovered =
          hoveredRblPointAnnotation &&
          enabledFindingTypes.radiographic_boneloss;

        const isBoxHovered =
          hoveredBoxAnnotation &&
          enabledFindingTypes[hoveredBoxAnnotation.type];

        // Check if we should prevent hovering based on selected finding
        if (selectedFinding && selectedFindingImageId === activeImage.imageId) {
          // Only allow hovering of the selected finding
          const isSelectedFinding =
            selectedFinding.id === hoveredBoxAnnotation?.id ||
            (selectedFinding.type === 'rbl' &&
              hoveredRblPointAnnotation &&
              selectedFinding.x === hoveredRblPointAnnotation.x &&
              selectedFinding.y === hoveredRblPointAnnotation.y);

          if (!isSelectedFinding) {
            return;
          }
        }

        if (!hoveredFinding) {
          setFindingState({
            finding: hoveredBoxAnnotation || hoveredRblPointAnnotation || null,
            position: e.detail.currentPoints.client,
          });
          setIsPopoverHovered(true);

          if (isBoxHovered) {
            resetAnnotationHoverStates();
            hoveredBoxAnnotation.hover = true;
          }

          if (isRblHovered) {
            resetAnnotationHoverStates();
            hoveredRblPointAnnotation.hover = true;
          }
        }
        // set the pointer because is shortest path for later search, give priority over boxes to consider hovering to a precise point
        // otherwise will Set hoveredBox if it is from boxNotation
        if (isRblHovered) {
          setHoveredRBLPoints(point);
        } else if (isBoxHovered) {
          setHoveredBox(hoveredBoxAnnotation.id);
        } else {
          // free references to hoveredFinding
          setHoveredBox(null);
          setHoveredRBLPoints(null);
        }
      }
    },
    [
      renderingEngine,
      viewportId,
      predictedPathologies,
      userAnnotations,
      rblFindings,
      isThumbnail,
      viewerMode,
      activeImage,
      incipientThresholdRange,
      isWithinConfidenceThreshold,
      enabledFindingTypes,
      selectedFinding,
      selectedFindingImageId,
      hoveredFinding,
      resetAnnotationHoverStates,
    ],
  );

  const checkForClickedFindings = useCallback(
    (e: any) => {
      const viewportForImage =
        renderingEngine && renderingEngine.getViewport(viewportId);
      if (!viewportForImage) {
        return;
      }
      let imageCoords: Types.Point2 | undefined;
      try {
        imageCoords = worldToImageCoords(
          viewportForImage.getCurrentImageId(),
          e.detail.currentPoints.world,
        );
      } catch (error) {
        return;
      }

      if (!imageCoords) {
        return;
      }
      const point = {
        x: imageCoords[0],
        y: imageCoords[1],
      };
      const pathologiesExist =
        predictedPathologies?.length ||
        userAnnotations?.length ||
        rblFindings?.length;
      if (
        !isThumbnail &&
        viewerMode !== AnnotatorConstants.MODES.MAGNIFYING &&
        pathologiesExist
      ) {
        let clickedBoxAnnotation = findHoveredAnnotation(
          predictedPathologies,
          userAnnotations,
          point,
        );

        const meetsSensitivityRequirement =
          clickedBoxAnnotation &&
          ((clickedBoxAnnotation.type !== CARIES &&
            clickedBoxAnnotation.type !== AREA_OF_CONCERN) ||
            isWithinSensitivityRange(
              clickedBoxAnnotation,
              activeImage,
              incipientThresholdRange,
            ));

        const shouldShowFinding =
          meetsSensitivityRequirement &&
          isWithinConfidenceThreshold(
            clickedBoxAnnotation.type,
            clickedBoxAnnotation.confidenceScore,
            clickedBoxAnnotation?.bin,
          );

        if (!shouldShowFinding) {
          clickedBoxAnnotation = null;
        }

        const clickedRblPointAnnotation = rblFindings?.length
          ? findHoveredRblPointAnnotation(point, rblFindings)
          : null;

        const isRblClicked =
          clickedRblPointAnnotation &&
          enabledFindingTypes.radiographic_boneloss;

        const isBoxClicked =
          clickedBoxAnnotation &&
          enabledFindingTypes[clickedBoxAnnotation.type];

        // Check if we should prevent clicking based on selected finding
        if (selectedFinding && selectedFindingImageId === activeImage.imageId) {
          // Only allow clicking of the selected finding
          const isSelectedFinding =
            selectedFinding.id === clickedBoxAnnotation?.id ||
            (selectedFinding.type === 'rbl' &&
              clickedRblPointAnnotation &&
              selectedFinding.x === clickedRblPointAnnotation.x &&
              selectedFinding.y === clickedRblPointAnnotation.y);

          if (!isSelectedFinding) {
            return;
          }
        }

        // Handle the clicked finding - prioritize RBL over other findings
        if (isRblClicked) {
          // RBL takes priority - always use RBL if it's detected
          setFindingState({
            finding: clickedRblPointAnnotation,
            position: e.detail.currentPoints.client,
          });
          setIsPopoverHovered(true);
          resetAnnotationHoverStates();
          clickedRblPointAnnotation.hover = true;
        } else if (isBoxClicked) {
          // Only use box annotation if no RBL was detected
          setFindingState({
            finding: clickedBoxAnnotation,
            position: e.detail.currentPoints.client,
          });
          setIsPopoverHovered(true);
          resetAnnotationHoverStates();
          clickedBoxAnnotation.hover = true;
        } else {
          // Clicked outside of any finding, close the popover
          setFindingState({ finding: null, position: undefined });
          setIsPopoverHovered(false);
          resetAnnotationHoverStates();
        }
      }
    },
    [
      renderingEngine,
      viewportId,
      predictedPathologies,
      userAnnotations,
      rblFindings,
      isThumbnail,
      viewerMode,
      activeImage,
      incipientThresholdRange,
      isWithinConfidenceThreshold,
      enabledFindingTypes,
      selectedFinding,
      selectedFindingImageId,
      resetAnnotationHoverStates,
    ],
  );

  /**
   * For the list of systemFindings and userAnnotations, add them to CornerstoneTools' state, which displays them in our viewer
   *
   * @param {*} systemFindings
   * @param {*} userAnnotations
   */

  const handleCornerstoneMouseDown = (e: any) => {
    prevMouseEvent = e.detail.event;
    const viewportForImage =
      renderingEngine && renderingEngine.getViewport(viewportId);

    // Check for clicked findings if click-based interaction is enabled and not in drawing mode
    if (
      shouldUseClickBasedInteraction &&
      viewerMode !== AnnotatorConstants.MODES.DRAWING
    ) {
      checkForClickedFindings(e);
    }

    if (
      viewerMode === AnnotatorConstants.MODES.DRAWING &&
      dicomCanvas &&
      viewportForImage
    ) {
      const width = 100;
      const height = 100;

      const imageCoords = worldToImageCoords(
        viewportForImage.getCurrentImageId(),
        e.detail.currentPoints.world,
      );
      if (!imageCoords) {
        return;
      }
      const annotation = {
        x: imageCoords[0] - width / 2,
        y: imageCoords[1] + height / 2,
        width,
        height,
      };
      const cornerstoneAnnotation = computeCornerstoneRoiFromSavedAnnotation(
        annotation,
        annotationDrawType === 'lucency'
          ? AnnotatorConstants.COLORS.lucency
          : AnnotatorConstants.COLORS.default,
      );

      cornerstoneAnnotation.type = annotationDrawType;

      addAnnotation(activeImage, cornerstoneAnnotation);

      // Return to editor mode, since dentists usually adjust contrast after each finding
      changeViewerMode?.(AnnotatorConstants.MODES.VIEWING);
    }
  };

  const handleCornerstoneMouseUp = (e: any) => {
    trackCornerstoneMouseEvent?.(
      activeImage,
      prevMouseEvent,
      e.detail.event,
      user,
    );

    if (modifiedFinding) {
      updateFinding(modifiedFinding);
      setModifiedFinding(null);
    }
  };

  const disableContextMenu = (e: any) => {
    e.preventDefault();
    return false;
  };

  const handleAnnotationEdit = (e: any) => {
    const { measurementData } = e.detail;
    if (measurementData && measurementData.objectId) {
      // We edited a user finding, which is allowed and will be saved once the mouse is lifted
      setModifiedFinding(e.detail.measurementData);
    }
  };

  /* eslint-disable-next-line react-hooks/exhaustive-deps -- The 'handleCornerstoneMouseMove' function makes the dependencies of useEffect Hook (at line 730) change on every render. Move it inside the useEffect callback. Alternatively, wrap the definition of 'handleCornerstoneMouseMove' in its own useCallback() Hook. */
  const handleCornerstoneMouseMove = useCallback(
    (e: any) => {
      if (!shouldUseClickBasedInteraction) {
        setTimeout(() => checkForHoveredFindings(e), 50);
      }
    },
    [shouldUseClickBasedInteraction, checkForHoveredFindings],
  );

  useEffect(() => {
    if (dicomCanvas && !isThumbnail && !shouldUseClickBasedInteraction) {
      dicomCanvas.addEventListener(
        Events.MOUSE_MOVE,
        handleCornerstoneMouseMove,
      );

      return () => {
        dicomCanvas.removeEventListener(
          Events.MOUSE_MOVE,
          handleCornerstoneMouseMove,
        );
      };
    }
  }, [
    dicomCanvas,
    isThumbnail,
    shouldUseClickBasedInteraction,
    predictedPathologies,
    userAnnotations,
    rblFindings,
    handleCornerstoneMouseMove,
  ]);

  /**
   * When the findings update, redraw them
   */
  useEffect(() => {
    if (activeImage && imageLoadStatus === 'success') {
      drawFindings();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'drawFindings'. Either include it or remove the dependency array. */
  }, [
    imageLoadStatus,
    enabledFindingTypes,
    hasHoveredFinding,
    activeImage,
    predictedPathologies,
    userAnnotations,
    rblFindings,
    incipientThresholdRange,
    practiceThreshold,
    shouldDisplayRootTips,
    confidenceScoreThreshold,
    isHoveringUserValidationFinding,
  ]);

  useEffect(() => {
    if (activeImage && imageLoadStatus === 'success') {
      const currentFinding = hoveredFinding;
      if (!currentFinding) {
        resetAnnotationHoverStates();
        drawFindings();
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'activeImage', 'drawFindings', 'imageLoadStatus', and 'resetAnnotationHoverStates'. Either include them or remove the dependency array. */
  }, [hoveredFinding]);

  useEffect(() => {
    if (!isAnnotationPopoverOpen) {
      resetAnnotationHoverStates();
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has a missing dependency: 'resetAnnotationHoverStates'. Either include it or remove the dependency array. */
  }, [isAnnotationPopoverOpen]);

  useEffect(() => {
    // When the component mounts, i.e empty dependency array we start timing
    // when the component unmounts we end the timer and track a image
    if (!isThumbnail) {
      return;
    }
    // Track how long we spend viewing each image
    const imageViewTime = performance.now();
    const accessedImage = { ...activeImage };

    return () => {
      const imageExitTime = performance.now();
      const secondsElapsed = Math.round(imageExitTime - imageViewTime) / 1000;
      trackEvent(mixpanelEvents.viewImageDuration, {
        objectId: accessedImage.objectId,
        duration: secondsElapsed,
        view: 'single-image-viewer',
        fileName: accessedImage.fileName,
        patientId: patient.id,
        detectedImageType: accessedImage.detectedImageType,
      });
    };

    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'activeImage', 'isThumbnail', 'patient.id', 'trackEvent', and 'trackImageView'. Either include them or remove the dependency array. */
  }, []);

  const onDeleteClick = () => {
    const { finding } = getCurrentFindingAndPosition();
    deleteUserAnnotation(activeImage, finding);
  };

  // Wrapper function on the validation selection, so that the local popover state will update
  const onValidationChange = (finding: any, option: any) => {
    const currentFinding = shouldUseClickBasedInteraction
      ? findingState.finding
      : hoveredFinding;

    if (shouldUseClickBasedInteraction && currentFinding) {
      const updatedFinding = {
        ...currentFinding,
        userValidation: option,
        validation: option,
      };
      setFindingState({
        ...findingState,
        finding: updatedFinding,
      });
      setHoveredFinding(updatedFinding);
    } else if (!shouldUseClickBasedInteraction && currentFinding) {
      const updatedFinding = {
        ...currentFinding,
        userValidation: option,
        validation: option,
      };
      setHoveredFinding(updatedFinding);
    }
    updateFinding(finding, option);
  };

  /**
   * Listen to changes in the activeImage (requiring dicomCanvas and cornerstone to be defined before executing)
   * On change of activeImage, update our listener so that a new annotation is added correctly
   */
  useEffect(() => {
    if (dicomCanvas && activeImage.objectId && isCornerstoneReady) {
      dicomCanvas.addEventListener('contextmenu', disableContextMenu);
      dicomCanvas.addEventListener(
        Events.MOUSE_DOWN,
        handleCornerstoneMouseDown,
      );
      dicomCanvas.addEventListener(Events.MOUSE_UP, handleCornerstoneMouseUp);
      dicomCanvas.addEventListener(
        Events.ANNOTATION_MODIFIED,
        handleAnnotationEdit,
      );
      return () => {
        // Returning this function is the Hooks way of cleaning up the side effects
        dicomCanvas.removeEventListener('contextmenu', disableContextMenu);
        dicomCanvas.removeEventListener(
          Events.MOUSE_DOWN,
          handleCornerstoneMouseDown,
        );
        dicomCanvas.removeEventListener(
          Events.MOUSE_UP,
          handleCornerstoneMouseUp,
        );
        dicomCanvas.removeEventListener(
          Events.ANNOTATION_MODIFIED,
          handleAnnotationEdit,
        );
      };
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useEffect has missing dependencies: 'handleCornerstoneMouseDown' and 'handleCornerstoneMouseUp'. Either include them or remove the dependency array. */
  }, [
    isCornerstoneReady,
    dicomCanvas,
    activeImage,
    changeViewerMode,
    viewerMode,
    modifiedFinding,
  ]);

  useEffect(() => {
    if (imageLoadStatus === 'success') {
      onResize();
    }
  }, [imageLoadStatus, onResize]);

  const showErrorIcon = imageLoadStatus === 'error';

  const getRblTooth = (finding: BoundingBoxFinding) => {
    const rblAnnotationIndex = findRBLAnnotationIndex(finding?.id, rblFindings);
    const rblParentFinding = rblFindings[rblAnnotationIndex[0]];
    return rblParentFinding?.toothNumber;
  };

  const setPopoverHovered = () => {
    setIsPopoverHovered(true);
  };

  const setPopoverNotHovered = () => {
    // Only close popover on mouse leave if not using click-based interaction
    if (!shouldUseClickBasedInteraction) {
      setIsPopoverHovered(false);
    }
  };

  /**
   * See if we're hovering the popover, or the backdrop (which has identifying characteristic z-index -1)
   *
   * @param {*} e
   */
  const onPopoverMouseMove = (e: any) => {
    const isHoveringPopover =
      e.target &&
      e.target.outerHTML &&
      !e.target.outerHTML.includes('z-index: -1');

    setIsPopoverHovered(isHoveringPopover);
  };
  const popoverTitle = (finding: any) => {
    if (finding.type === AREA_OF_CONCERN) {
      return AREA_OF_CONCERN_SINGULAR;
    }

    // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return typeToHeaderMap[finding.type];
  };

  const modelErrorMessage = isThumbnail
    ? getModelErrorMessage(activeImage)
    : null;

  const cornerstoneClasses = classNames({
    ['cornerstoneElement']: true,
    [cursorClass]: true,
    'patient-view': isImageWithSegmentations && showSegmentation,
    'thumbnail-view': isThumbnail,
    'tooth-parts':
      isImageWithSegmentations &&
      showSegmentation &&
      isSegmentationEnabled &&
      showAnatomySegmentation,
  });

  return (
    <StyleWrapper
      className="cornerstoneContainer"
      data-cy="singleFindingViewerImage"
      data-testid="singleFindingViewerImage"
      ref={containerRef}
    >
      {QualityAssessmentWidget}
      <SegmentationsHatchPatterns />
      {!isThumbnail &&
        shouldEnableImageQualityAssessment &&
        imageLoadStatus === 'success' && (
          <Button
            className="vai-absolute vai-top-4 vai-right-4 vai-z-[999] !vai-bg-zinc-900/95 !vai-bg-opacity-95"
            onClick={handleOpenOverlay}
            size="sm"
          >
            <FontAwesomeIcon
              icon={isOverlayOpen ? faTimes : faClipboardCheck}
              className="vai-mr-2"
            />
            {isOverlayOpen ? (
              <Trans>Close Assessment</Trans>
            ) : (
              <Trans>Assess Image Quality</Trans>
            )}
          </Button>
        )}
      {imageLoadStatus === 'loading' && (
        <div className="loadingSpinner">
          <CircularProgress />
        </div>
      )}
      {!showErrorIcon &&
        imageLoadStatus === 'success' &&
        isThumbnail &&
        currentlyHoveredImageId === activeImage.objectId && (
          <Button
            className="vai-absolute vai-bottom-[10%] vai-z-[1000] vai-p-[2px] !vai-bg-black !vai-bg-opacity-50 vai-h-8 vai-w-8"
            onMouseEnter={() =>
              // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              setIsRotationButtonHovered((p) => [...p, activeImage.objectId])
            }
            onMouseLeave={() =>
              // @ts-expect-error TS(2722): Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
              setIsRotationButtonHovered((p) =>
                p.filter((v) => v !== activeImage.objectId),
              )
            }
            size="icon"
            onClick={() => {
              rotateImage(activeImage);
            }}
          >
            <FontAwesomeIcon
              className="vai-text-slate-200"
              icon={faRotateRight}
            />
          </Button>
        )}
      <ImageChipStatus image={activeImage} isThumbnail={isThumbnail} />
      {showErrorIcon && <ErrorIcon className="errorIcon" />}
      {modelErrorMessage && (
        <ModelErrorIcon
          modelErrorMessage={modelErrorMessage}
          variant="thumbnail"
        />
      )}
      <div ref={dicomCanvasRef} className={cornerstoneClasses} />
      {(() => {
        const { finding, position } = getCurrentFindingAndPosition();
        return !!finding?.id &&
          position &&
          position[0] &&
          position[1] &&
          !isCasePresentationMode ? (
          <>
            <Popover
              anchorReference="anchorPosition"
              anchorPosition={{
                top: (position[1] || 0) + 10,
                left: (position[0] || 0) + 10,
              }}
              open={isAnnotationPopoverOpen}
              className="findingPopover"
              style={{
                pointerEvents: 'auto',
              }}
              onClose={() => {
                setFindingState({ finding: null, position: undefined });
                if (!shouldUseClickBasedInteraction) {
                  setHoveredFinding(null);
                  setHoveredBox(null);
                  setHoveredRBLPoints(null);
                }
                resetAnnotationHoverStates();
              }}
              PaperProps={{
                onMouseEnter: setPopoverHovered,
                onMouseLeave: setPopoverNotHovered,
              }}
              onMouseMove={hoveredLine ? onPopoverMouseMove : undefined}
            >
              {finding.rblType ? (
                <>
                  <FindingPopoverContainers
                    className="hoverPopupContainer"
                    data-testid="image-popup"
                  >
                    <VideaTypography
                      variant="bodySmall"
                      className="popupHeaderText"
                    >
                      {finding.rblType === 'root' ? (
                        <Trans>Apex</Trans>
                      ) : (
                        <Trans>Radiographic Bone Level</Trans>
                      )}
                    </VideaTypography>
                    <CloseIcon
                      className="popupCloseIcon"
                      onClick={() => {
                        setFindingState({ finding: null, position: undefined });
                        if (!shouldUseClickBasedInteraction) {
                          setHoveredFinding(null);
                          setHoveredBox(null);
                          setHoveredRBLPoints(null);
                        }
                        resetAnnotationHoverStates();
                      }}
                    />
                  </FindingPopoverContainers>
                  <FindingPopoverContainers className="findingPopoverToothNumberContainer">
                    <RblPopup
                      finding={finding}
                      rblFindings={rblFindings}
                      isPAImage={isPAImage}
                    />
                  </FindingPopoverContainers>
                </>
              ) : (
                <>
                  <FindingPopoverContainers
                    className="hoverPopupContainer"
                    data-testid="image-popup"
                  >
                    <VideaTypography
                      variant="bodySmall"
                      className="popupHeaderText"
                    >
                      {popoverTitle(finding)}
                      {showDevtools && ` - ${finding.binSource}`}
                    </VideaTypography>
                    <CloseIcon
                      className="popupCloseIcon"
                      onClick={() => {
                        setFindingState({ finding: null, position: undefined });
                        if (!shouldUseClickBasedInteraction) {
                          setHoveredFinding(null);
                          setHoveredBox(null);
                          setHoveredRBLPoints(null);
                        }
                        resetAnnotationHoverStates();
                      }}
                    />
                  </FindingPopoverContainers>
                  {finding.type !== CALCULUS && (
                    <FindingPopoverContainers className="findingPopoverInnerContainer">
                      {finding.type === 'caries' &&
                        finding.depth === CariesDepth.into_enamel && (
                          <VideaTypography
                            variant="bodyExtrasmall"
                            className="popupHeaderText"
                          >
                            <Trans>(Potential Incipient Caries)</Trans>
                          </VideaTypography>
                        )}
                      <FindingValidationSelector
                        finding={finding}
                        findingGroupType={finding.type}
                        isFullScreenViewer={false}
                        updateFinding={onValidationChange}
                        onDeleteClick={onDeleteClick}
                      />
                    </FindingPopoverContainers>
                  )}
                </>
              )}
              <FindingPopoverContainers className="findingPopoverInnerContainer">
                <ShowOnlyFindingButton finding={finding} />
              </FindingPopoverContainers>
            </Popover>
          </>
        ) : null;
      })()}
    </StyleWrapper>
  );
};

export default FindingViewer;
